import { Stack } from "@mui/material";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import useClientContext from "../../../../../../../context/useClientContext";
import { useEnhancedBiApiClientProvider } from "../../../contexts/ApiClientProviderContext";
import {
  selectCurrentReport,
  selectIsReportChanged,
  selectReportEditMode,
} from "../../../store/selectors/currentReportSelectors";
import BackToButton from "../../common/BackToButton";
import SaveReportDialog from "../common/saving/SaveReportDialog";
import UnsavedChangesDialog from "./UnsavedChangesDialog";

export default function BackToReports({
  ProductName,
  readonlyMode,
}: {
  ProductName?: React.ReactElement;
  readonlyMode?: boolean;
}) {
  const navigate = useNavigate();
  const { clientCode } = useClientContext();
  const isReportChanged = useSelector(selectIsReportChanged);
  const report = useSelector(selectCurrentReport);
  const editMode = useSelector(selectReportEditMode);
  const [showUnsavedDialog, setShowUnsavedDialog] = React.useState(false);
  const [showSaveDialog, setShowSaveDialog] = React.useState(false);
  const { getReportsUrl } = useEnhancedBiApiClientProvider();

  const navigateToReports = React.useCallback(() => {
    navigate(getReportsUrl(clientCode));
  }, [clientCode, getReportsUrl, navigate]);

  const onBackButtonClicked = React.useCallback(() => {
    if (editMode === true && isReportChanged === true) {
      setShowUnsavedDialog(true);
    } else {
      navigateToReports();
    }
  }, [isReportChanged, editMode, navigateToReports]);

  const handleOnCancel = React.useCallback(() => setShowUnsavedDialog(false), []);
  const handleOnContinue = React.useCallback(() => navigateToReports(), [navigateToReports]);
  const handleOnSave = React.useCallback(() => {
    setShowSaveDialog(true);
    setShowUnsavedDialog(false);
  }, []);

  const DialogComponent = useMemo(() => {
    if (showUnsavedDialog && report) {
      return (
        <UnsavedChangesDialog
          onCancel={handleOnCancel}
          onContinue={handleOnContinue}
          onSave={handleOnSave}
          name={report.name}
          type="report"
        />
      );
    }
    return null;
  }, [showUnsavedDialog, report, handleOnCancel, handleOnContinue, handleOnSave]);

  return (
    <>
      <Stack
        direction="row"
        useFlexGap
        spacing={1.4}
        sx={{
          flexWrap: "nowrap",
          alignItems: "center",
        }}
      >
        <BackToButton onClick={onBackButtonClicked} ProductName={ProductName} readonlyMode={readonlyMode} />
      </Stack>
      {DialogComponent}
      {showSaveDialog && report && (
        <SaveReportDialog
          report={report}
          open={true}
          title="Save Report"
          saveBtnTitle="Save"
          onClose={() => setShowSaveDialog(false)}
        />
      )}
    </>
  );
}
